import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2, Ticket as TicketIcon, Wrench, Users, Presentation, Settings, AlertTriangle } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// UK financial year runs 1 Apr – 31 Mar
function fyStart(ref) {
  const y = ref.getMonth() >= 3 ? ref.getFullYear() : ref.getFullYear() - 1;
  return new Date(y, 3, 1);
}

function rangeFor(preset) {
  const today = new Date();
  const end = new Date(today);
  const start = new Date(today);
  if (preset === "1m") start.setMonth(start.getMonth() - 1);
  else if (preset === "6m") start.setMonth(start.getMonth() - 6);
  else if (preset === "12m") start.setMonth(start.getMonth() - 12);
  else if (preset === "fytd") return { start: iso(fyStart(today)), end: iso(today) };
  else if (preset === "lastfy") {
    const thisFy = fyStart(today);
    const lastFyStart = new Date(thisFy.getFullYear() - 1, 3, 1);
    const lastFyEnd = new Date(thisFy.getFullYear(), 2, 31); // 31 Mar
    return { start: iso(lastFyStart), end: iso(lastFyEnd) };
  }
  return { start: iso(start), end: iso(end) };
}

const PRESETS = [
  { v: "1m", l: "Last 1 month" },
  { v: "6m", l: "Last 6 months" },
  { v: "12m", l: "Last 12 months" },
  { v: "lastfy", l: "Last financial year" },
  { v: "fytd", l: "Financial YTD" },
  { v: "custom", l: "Custom" },
];

function Kpi({ icon: Icon, label, value, sub, tone }) {
  return (
    <Card data-testid={`tech-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</span>
          <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${tone}`}><Icon className="h-4 w-4" /></span>
        </div>
        <div className="mt-3 text-3xl font-bold text-slate-900 font-heading">{value}</div>
        {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
      </CardContent>
    </Card>
  );
}

export default function TechnicalTab() {
  const [preset, setPreset] = useState("6m");
  const [custom, setCustom] = useState(() => rangeFor("6m"));
  const [data, setData] = useState(null);
  const [cap, setCap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCfg, setShowCfg] = useState(false);
  const [drill, setDrill] = useState(null); // { engineer, events }

  const range = useMemo(() => (preset === "custom" ? custom : rangeFor(preset)), [preset, custom]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get(`/reports/technical/summary?start=${range.start}&end=${range.end}`),
        api.get(`/reports/technical/capacity?start=${range.start}&end=${range.end}`),
      ]);
      setData(r.data); setCap(c.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const [r, c] = await Promise.all([
          api.get(`/reports/technical/summary?start=${range.start}&end=${range.end}`),
          api.get(`/reports/technical/capacity?start=${range.start}&end=${range.end}`),
        ]);
        if (!cancel) { setData(r.data); setCap(c.data); }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [range.start, range.end]);

  const reload = load;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex rounded-md border border-slate-200 overflow-hidden flex-wrap" data-testid="tech-period-toggle">
          {PRESETS.map((p) => (
            <button
              key={p.v}
              onClick={() => setPreset(p.v)}
              className={`px-3 py-1.5 text-xs font-medium border-r border-slate-200 last:border-r-0 transition-colors ${preset === p.v ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid={`tech-period-${p.v}`}
            >
              {p.l}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex items-center gap-2" data-testid="tech-custom-range">
            <Input type="date" value={custom.start} max={custom.end} className="h-8 w-40 text-xs"
              onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))} data-testid="tech-custom-start" />
            <span className="text-slate-400 text-xs">to</span>
            <Input type="date" value={custom.end} min={custom.start} className="h-8 w-40 text-xs"
              onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))} data-testid="tech-custom-end" />
          </div>
        )}
      </div>

      <div className="text-[11px] font-mono text-slate-400">
        Showing {range.start} → {range.end}{loading ? " · loading…" : ""}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi
          icon={CalendarClock}
          tone="bg-blue-50 text-blue-600"
          label="Time to Onboard"
          value={`${data?.avg_onboard_working_days ?? 0} days`}
          sub={`Working days · start → close · ${data?.projects_completed ?? 0} projects`}
        />
        <Kpi
          icon={CheckCircle2}
          tone="bg-emerald-50 text-emerald-600"
          label="Delivered On Time"
          value={`${data?.on_time_rate ?? 0}%`}
          sub={`${data?.on_time_count ?? 0} of ${data?.projects_with_target ?? 0} projects with a target date`}
        />
        <Kpi
          icon={TicketIcon}
          tone="bg-indigo-50 text-indigo-600"
          label="Tickets Closed"
          value={data?.closed_tickets ?? 0}
          sub="Closed in the selected period"
        />
        <Kpi
          icon={Wrench}
          tone="bg-amber-50 text-amber-600"
          label="First Line Fix Rate"
          value={`${data?.first_line_rate ?? 0}%`}
          sub="Closed with no vendor ticket number"
        />
      </div>

      <Card data-testid="tech-capacity-table">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="h-8 w-8 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center"><Users className="h-4 w-4" /></span>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 font-heading">Team capacity</h3>
                <p className="text-[11px] text-slate-500">Actual engineer hours in the selected period</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowCfg((v) => !v)}
                className="h-8 w-8 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center transition-colors"
                data-testid="tech-capacity-settings-btn"
                title="Webinar / demo calendar settings"
              >
                <Settings className="h-4 w-4" />
              </button>
              <div className="text-right">
                <div className="text-2xl font-bold text-slate-900 font-heading">{cap?.totals?.total_hours ?? 0}h</div>
                <div className="text-[11px] text-slate-500">total</div>
              </div>
            </div>
          </div>

          {showCfg && <WebinarConfig onClose={() => setShowCfg(false)} onSaved={() => { setShowCfg(false); reload(); }} />}

          {cap?.webinar_status && cap.webinar_status.enabled && (cap.webinar_status.errors?.length > 0) && (
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800" data-testid="tech-webinar-warning">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">Some calendars couldn't be read.</div>
                <div className="text-amber-700">Usually the Graph app still needs the <span className="font-mono">Calendars.Read</span> application permission + admin consent. Affected: {cap.webinar_status.errors.map((e) => e.mailbox).join(", ")}.</div>
              </div>
            </div>
          )}

          {(cap?.engineers || []).length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No time booked in this period.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    <th className="py-2 pr-3 font-medium">Engineer</th>
                    <th className="py-2 px-3 font-medium text-right">Project</th>
                    <th className="py-2 px-3 font-medium text-right">Support</th>
                    <th className="py-2 px-3 font-medium text-right">Service</th>
                    <th className="py-2 px-3 font-medium text-right">Webinar/Demo</th>
                    <th className="py-2 pl-3 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {cap.engineers.map((e) => (
                    <tr key={e.engineer} className="border-b border-slate-50 last:border-0" data-testid={`tech-capacity-row-${e.engineer}`}>
                      <td className="py-2 pr-3 font-medium text-slate-800">{e.engineer}</td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-600">{e.project_hours}h</td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-600">{e.support_hours}h</td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-600">{e.service_hours}h</td>
                      <td className="py-2 px-3 text-right tabular-nums text-slate-600">
                        {(e.webinar_events?.length > 0) ? (
                          <button
                            onClick={() => setDrill({ engineer: e.engineer, events: e.webinar_events })}
                            className="tabular-nums text-violet-600 hover:text-violet-800 hover:underline font-medium"
                            data-testid={`tech-webinar-hours-${e.engineer}`}
                            title="View the webinars/demos making up this total"
                          >
                            {e.webinar_hours}h
                          </button>
                        ) : (
                          <span>{e.webinar_hours ?? 0}h</span>
                        )}
                      </td>
                      <td className="py-2 pl-3 text-right tabular-nums font-semibold text-slate-900">{e.total_hours}h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <WebinarDrillDialog drill={drill} onClose={() => setDrill(null)} />
    </div>
  );
}

function fmtEventTime(startIso, endIso) {
  const parse = (s) => {
    if (!s) return null;
    const clean = String(s).split(".")[0].replace("Z", "");
    const d = new Date(clean);
    return isNaN(d.getTime()) ? null : d;
  };
  const a = parse(startIso);
  const b = parse(endIso);
  if (!a) return { date: "—", time: "" };
  const date = a.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  const t = (d) => d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const time = b ? `${t(a)}–${t(b)}` : t(a);
  return { date, time };
}

function WebinarDrillDialog({ drill, onClose }) {
  const events = drill?.events || [];
  const totalMins = events.reduce((s, e) => s + (e.duration_minutes || 0), 0);
  return (
    <Dialog open={!!drill} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="tech-webinar-drill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Presentation className="h-4 w-4 text-violet-600" />
            Webinars / demos — {drill?.engineer}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription className="text-xs text-slate-500 -mt-2 mb-1">
          {events.length} event{events.length === 1 ? "" : "s"} · {(totalMins / 60).toFixed(2)}h total
        </DialogDescription>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
          {events.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400">No calendar items in this period.</div>
          ) : (
            events.map((ev, i) => {
              const { date, time } = fmtEventTime(ev.start, ev.end);
              return (
                <div key={i} className="py-3 flex items-start justify-between gap-4" data-testid={`webinar-drill-row-${i}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm text-slate-900 truncate">{ev.subject}</div>
                    <div className="text-[12px] text-slate-500 mt-0.5">{date} · {time}</div>
                    {(ev.categories || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {ev.categories.map((c) => (
                          <span key={c} className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-violet-50 text-violet-700 border border-violet-200">{c}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 text-right tabular-nums text-sm font-semibold text-slate-800">{ev.duration_hours}h</div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WebinarConfig({ onClose, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/reports/capacity/webinar-config`).then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);

  const listToText = (arr) => (arr || []).join("\n");
  const textToList = (t) => t.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: cfg.enabled,
        mailboxes: cfg.mailboxes,
        match_categories: cfg.match_categories,
        match_keywords: cfg.match_keywords,
      };
      await api.put(`/reports/capacity/webinar-config`, payload);
      toast.success("Calendar capacity settings saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save (admin only)");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return null;
  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="tech-webinar-config">
      <div className="flex items-center gap-2">
        <Presentation className="h-4 w-4 text-violet-600" />
        <h4 className="text-sm font-semibold text-slate-800">Webinar / demo calendar tracking</h4>
      </div>
      <p className="text-[12px] text-slate-500">
        Reads the mailboxes below via Microsoft 365. An event counts as a webinar/demo if it has one of the
        Outlook <span className="font-medium">categories</span> below, or its subject contains one of the
        <span className="font-medium"> keywords</span>. Tip: create an Outlook category called "Webinar" and apply it to those meetings.
      </p>
      <div className="flex items-center gap-2">
        <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="webinar-enabled-switch" />
        <span className="text-xs text-slate-600">Enabled</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Mailboxes (one per line)</label>
          <textarea
            className="mt-1 w-full h-24 rounded-md border border-slate-200 p-2 text-xs font-mono"
            value={listToText(cfg.mailboxes)}
            onChange={(e) => setCfg({ ...cfg, mailboxes: textToList(e.target.value) })}
            data-testid="webinar-mailboxes-input"
          />
        </div>
        <div>
          <label className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Outlook categories</label>
          <textarea
            className="mt-1 w-full h-24 rounded-md border border-slate-200 p-2 text-xs font-mono"
            value={listToText(cfg.match_categories)}
            onChange={(e) => setCfg({ ...cfg, match_categories: textToList(e.target.value) })}
            data-testid="webinar-categories-input"
          />
        </div>
        <div>
          <label className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Subject keywords</label>
          <textarea
            className="mt-1 w-full h-24 rounded-md border border-slate-200 p-2 text-xs font-mono"
            value={listToText(cfg.match_keywords)}
            onChange={(e) => setCfg({ ...cfg, match_keywords: textToList(e.target.value) })}
            data-testid="webinar-keywords-input"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose} data-testid="webinar-cfg-cancel">Cancel</Button>
        <Button size="sm" className="bg-violet-600 hover:bg-violet-700" onClick={save} disabled={saving} data-testid="webinar-cfg-save">
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}
